import React, { useState, useCallback, useEffect } from 'react';
import {
  Row,
  Col,
  Text,
  Button,
  Whisper,
  Tooltip,
  FlexboxGrid,
  Grid,
} from 'rsuite';
import { isNullUndefinedOrEmptyString, titleize } from '../../common/utils';
import usePlaceNavigate from '../../hooks/usePlaceNavigate';
import { useDispatch, useSelector } from 'react-redux';
import { initiateRenewFlow } from '../../store/slices/renew/renewThunks';
import {
  selectInitiatingRenewFlow,
  selectInitiatingRenewFlowError,
  selectRenewApplicationNumber,
} from '../../store/slices/renew/renewSelectors';
import IconIndicator from '../user-feedback/IconIndicator';
import Notice from '../user-feedback/Notice';
import { selectConfigExpressRegistrationTypeVerbiage } from '../../store/slices/config/configSelectors';
import pluralize from 'pluralize';
import { License } from 'src/types/licenses';

interface PermitRowProps {
  license?: License;
  ownerLabel?: string;
}

const PermitRow: React.FC<PermitRowProps> = ({ license, ownerLabel }) => {
  const dispatch = useDispatch();
  const placeNavigate = usePlaceNavigate();

  const [showInitiateRenewFlowError, setShowInitiateRenewFlowError] =
    useState(false);
  const initiatingRenewFlow = useSelector(selectInitiatingRenewFlow);
  const licenseToBeRenewedApplicationNumber = useSelector(
    selectRenewApplicationNumber
  );
  const initiatingRenewFlowError = useSelector(selectInitiatingRenewFlowError);
  const registrationTypeVerbiage = useSelector(
    selectConfigExpressRegistrationTypeVerbiage
  );

  useEffect(() => {
    if (
      !initiatingRenewFlow &&
      license?.applicationNumber &&
      license.applicationNumber === licenseToBeRenewedApplicationNumber &&
      !isNullUndefinedOrEmptyString(initiatingRenewFlowError)
    ) {
      setShowInitiateRenewFlowError(true);
      setTimeout(() => {
        setShowInitiateRenewFlowError(false);
      }, 10000); // 10 seconds
    } else {
      setShowInitiateRenewFlowError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initiatingRenewFlow,
    licenseToBeRenewedApplicationNumber,
    license?.applicationNumber,
    initiatingRenewFlow,
  ]);

  const handleRenewClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      (e.target as HTMLButtonElement).blur();
      if (!initiatingRenewFlow && license?.applicationNumber) {
        dispatch(
          initiateRenewFlow(
            license?.applicationNumber,
            `/all-permits`,
            onFlowStepsMapped
          ) as any
        );
      }
    },
    [license]
  );

  const onFlowStepsMapped = (path?: string) => {
    if (path) {
      placeNavigate(`/${path}`);
    }
  };

  return (
    <FlexboxGrid
      as={Row}
      align="middle"
      className="all-permits-permit-row"
    >
      <FlexboxGrid.Item
        as={Col}
        xs={24}
        md={7}
        className="mb-4"
      >
        <Grid fluid>
          <Row>
            <Col
              xs={24}
              mdHidden={true}
              lgHidden={true}
              xlHidden={true}
              xxlHidden={true}
            >
              <Text className="text-small text-bold">Address</Text>
            </Col>
            <Col xs={24}>
              <Text>{titleize(license?.address || '')}</Text>
            </Col>
          </Row>
        </Grid>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item
        as={Col}
        xs={12}
        md={3}
        className="mb-4"
      >
        <Grid fluid>
          <Row>
            <Col
              xs={24}
              mdHidden={true}
              lgHidden={true}
              xlHidden={true}
              xxlHidden={true}
            >
              <Text className="text-small text-bold">
                {registrationTypeVerbiage} Number
              </Text>
            </Col>
            <Col xs={24}>
              <Text>{license?.licenseNumber ?? '-'}</Text>
            </Col>
          </Row>
        </Grid>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item
        as={Col}
        xs={12}
        md={3}
        className="mb-4"
      >
        <Grid fluid>
          <Row>
            <Col
              xs={24}
              mdHidden={true}
              lgHidden={true}
              xlHidden={true}
              xxlHidden={true}
            >
              <Text className="text-small text-bold">Status</Text>
            </Col>
            <Col xs={24}>
              <Text>{titleize(license?.status ?? '-')}</Text>
            </Col>
          </Row>
        </Grid>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item
        as={Col}
        xs={12}
        md={3}
        className="mb-4"
      >
        <Grid fluid>
          <Row>
            <Col
              xs={24}
              mdHidden={true}
              lgHidden={true}
              xlHidden={true}
              xxlHidden={true}
            >
              <Text className="text-small text-bold">Expiration Date</Text>
            </Col>
            <Col xs={24}>
              <Text>{license?.expirationDate ?? '-'}</Text>
            </Col>
          </Row>
        </Grid>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item
        as={Col}
        xs={24}
        md={3}
        className="mb-4"
      >
        <Grid fluid>
          <Row>
            <Col
              xs={24}
              mdHidden={true}
              lgHidden={true}
              xlHidden={true}
              xxlHidden={true}
            >
              <Text className="text-small text-bold">
                {pluralize(ownerLabel ?? '')} Name
              </Text>
            </Col>
            <Col xs={24}>
              {license?.ownersNameToolTip ? (
                <Whisper
                  trigger="hover"
                  speaker={<Tooltip>{license?.ownersNameToolTip}</Tooltip>}
                >
                  <Text>{license?.ownersNames}</Text>
                </Whisper>
              ) : (
                <Text>{license?.ownersNames}</Text>
              )}
            </Col>
          </Row>
        </Grid>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item
        as={Col}
        xs={24}
        md={5}
      >
        <div role="grid">
          <FlexboxGrid.Item as={Row}>
            <FlexboxGrid.Item
              as={Col}
              xs={24}
              sm={12}
            >
              {license?.canRenew && (
                <Button
                  style={{ width: '100%' }}
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    handleRenewClick(e as any);
                  }}
                  className="btn-tertiary"
                >
                  Renew
                  {initiatingRenewFlow &&
                    licenseToBeRenewedApplicationNumber ===
                      license?.applicationNumber && (
                      <IconIndicator
                        color="var(--soft-blue)"
                        size={16}
                        textStyle={{ marginLeft: '5px' }}
                      />
                    )}
                </Button>
              )}
            </FlexboxGrid.Item>
            <FlexboxGrid.Item
              as={Col}
              xs={24}
              sm={12}
            >
              <Button
                className="btn-tertiary"
                style={{ width: '100%' }}
                onClick={() =>
                  placeNavigate(`/single-permit/${license?.applicationNumber}`)
                }
              >
                View
              </Button>
            </FlexboxGrid.Item>
          </FlexboxGrid.Item>
        </div>
      </FlexboxGrid.Item>
      {showInitiateRenewFlowError && (
        <FlexboxGrid.Item
          as={Col}
          xs={24}
          style={{ marginTop: '10px' }}
        >
          <Notice message={initiatingRenewFlowError} />
        </FlexboxGrid.Item>
      )}
    </FlexboxGrid>
  );
};

export default PermitRow;
